import { useEffect, useRef, useState } from 'react';
import { useFocusTrap } from '../../utils/focusTrap';
import { CopyButton } from '../shared';
import styles from '../CreatePublishableTokenDialog.module.css';

interface GenerateWorkerTokenDialogProps {
  profileId: string;
  profileName: string;
  onClose: () => void;
  generateWorkerToken: (profileId: string, expiresInSeconds?: number) => Promise<{
    success: boolean;
    token?: string;
    kid?: string;
    expiresInSeconds?: number;
    error?: string;
  }>;
}

const EXPIRY_PRESETS: { label: string; seconds: number | null }[] = [
  { label: '1 day', seconds: 86400 },
  { label: '7 days', seconds: 604800 },
  { label: '30 days', seconds: 2592000 },
  { label: '90 days', seconds: 7776000 },
  { label: '1 year', seconds: 31536000 },
  { label: 'Custom', seconds: -1 },
];

const CUSTOM_UNITS: { label: string; multiplier: number }[] = [
  { label: 'hours', multiplier: 3600 },
  { label: 'days', multiplier: 86400 },
];

export function GenerateWorkerTokenDialog({
  profileId,
  profileName,
  onClose,
  generateWorkerToken,
}: GenerateWorkerTokenDialogProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  const [expiryPreset, setExpiryPreset] = useState<number>(7776000); // 90 days default
  const [customAmount, setCustomAmount] = useState('');
  const [customUnit, setCustomUnit] = useState(86400); // days

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);

  useFocusTrap(modalRef, true);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isCustomExpiry = expiryPreset === -1;

  const computeExpiresInSeconds = (): number | undefined => {
    if (isCustomExpiry) {
      const amount = parseInt(customAmount, 10);
      if (!amount || amount <= 0) return undefined;
      return amount * customUnit;
    }
    return expiryPreset;
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError('');

    try {
      const expiresInSeconds = computeExpiresInSeconds();
      const result = await generateWorkerToken(profileId, expiresInSeconds);

      if (result.success && result.token) {
        setGeneratedToken(result.token);
      } else {
        setError(result.error || 'Failed to generate worker token');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to generate worker token');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div
      className={styles.modalOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="generate-worker-token-title"
    >
      <div ref={modalRef} className={styles.modal} onClick={e => e.stopPropagation()}>
        <h2 id="generate-worker-token-title" className={styles.modalTitle}>
          Generate Worker Token
        </h2>

        <div className={styles.hint} style={{ marginBottom: 'var(--space-3)' }}>
          Profile: <strong>{profileName}</strong>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {generatedToken ? (
          <>
            <div className={styles.formGroup}>
              <label className={styles.label}>Worker Admission Token</label>
              <div style={{ position: 'relative' }}>
                <textarea
                  readOnly
                  value={generatedToken}
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: 'var(--space-2)',
                    fontSize: 'var(--text-xs)',
                    fontFamily: 'var(--font-mono, monospace)',
                    border: 'var(--border-width) solid var(--border)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    wordBreak: 'break-all',
                  }}
                />
                <div style={{ marginTop: 'var(--space-2)' }}>
                  <CopyButton value={generatedToken} aria-label="Copy token" />
                </div>
              </div>
              <div className={styles.hint}>
                This token will not be shown again. Copy it now.
              </div>
            </div>

            <div className={styles.buttonGroup}>
              <button
                type="button"
                onClick={onClose}
                className={styles.button}
              >
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Expiry */}
            <div className={styles.formGroup}>
              <label htmlFor="worker-token-expiry" className={styles.label}>Expires</label>
              <select
                id="worker-token-expiry"
                className={styles.select}
                value={String(expiryPreset)}
                onChange={e => setExpiryPreset(Number(e.target.value))}
              >
                {EXPIRY_PRESETS.map(p => (
                  <option key={p.label} value={String(p.seconds)}>
                    {p.label}
                  </option>
                ))}
              </select>
              {isCustomExpiry && (
                <div className={styles.expiryCustomRow}>
                  <input
                    type="number"
                    className={styles.input}
                    value={customAmount}
                    onChange={e => setCustomAmount(e.target.value)}
                    placeholder="Amount"
                    min="1"
                  />
                  <select
                    className={styles.select}
                    value={customUnit}
                    onChange={e => setCustomUnit(Number(e.target.value))}
                  >
                    {CUSTOM_UNITS.map(u => (
                      <option key={u.label} value={u.multiplier}>{u.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className={styles.buttonGroup}>
              <button
                type="button"
                onClick={onClose}
                className={`${styles.button} ${styles.buttonSecondary}`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating || (isCustomExpiry && (!customAmount || parseInt(customAmount, 10) <= 0))}
                className={styles.button}
              >
                {isGenerating ? 'Generating\u2026' : 'Generate'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
